export const AGENT_REGISTRY = {
    'claude-code': {
        shortName: 'claude-code',
        displayName: 'Claude Code',
        description: 'Anthropic Claude Code via @agentclientprotocol/claude-agent-acp',
        command: 'npx',
        args: ['-y', '@agentclientprotocol/claude-agent-acp'],
        aliases: [
            // The original Zed-scoped package is deprecated as of late 2026,
            // but still very common in existing captures + IDE configs.
            ['npx', '-y', '@zed-industries/claude-code-acp'],
        ],
    },
    codex: {
        shortName: 'codex',
        displayName: 'Codex',
        description: 'OpenAI Codex via @zed-industries/codex-acp',
        command: 'npx',
        args: ['-y', '@zed-industries/codex-acp'],
    },
    goose: {
        shortName: 'goose',
        displayName: 'Goose',
        description: 'Block Goose — install separately, then this runs `goose acp`',
        command: 'goose',
        args: ['acp'],
        requiresExternalInstall: true,
    },
    opencode: {
        shortName: 'opencode',
        displayName: 'OpenCode',
        description: 'SST OpenCode — install via opencode.ai/install, then `opencode acp`',
        command: 'opencode',
        args: ['acp'],
        requiresExternalInstall: true,
    },
    junie: {
        shortName: 'junie',
        displayName: 'Junie',
        description: 'JetBrains Junie CLI — install separately, then this runs `junie --acp=true`',
        command: 'junie',
        args: ['--acp=true'],
        requiresExternalInstall: true,
    },
};
/** The agent picked when `acp-devtools` is launched with no arguments. */
export const DEFAULT_AGENT = 'claude-code';
/** Look up a definition by short name. Throws with a helpful message if unknown. */
export function resolveAgent(shortName) {
    const agent = AGENT_REGISTRY[shortName];
    if (!agent) {
        const available = Object.keys(AGENT_REGISTRY).sort().join(', ');
        throw new Error(`unknown agent "${shortName}". Known shortcuts: ${available}.`);
    }
    return agent;
}
/** True iff this short name is a registered agent shortcut. */
export function isAgentShortcut(name) {
    return Object.prototype.hasOwnProperty.call(AGENT_REGISTRY, name);
}
/** Snapshot of the registry for UIs and help text. */
export function listAgents() {
    return Object.values(AGENT_REGISTRY).slice();
}
/**
 * Reverse-look-up an agent definition from a captured `agentCommand` string
 * (typically of the form `command arg1 arg2 …` as recorded by the proxy).
 * Returns the matching definition when the command + args prefix matches a
 * known shortcut, so the UI can replace the verbose form with a friendly
 * label. Returns `null` for custom agents.
 *
 * The first token is compared both verbatim and by basename, so an
 * absolute-path launch — which JetBrains always uses (`/opt/homebrew/bin/goose
 * acp`) — still resolves to its shortcut instead of falling back to the raw
 * path.
 */
export function detectAgentShortcut(agentCommand) {
    const trimmed = agentCommand.trim();
    if (trimmed.length === 0)
        return null;
    const candidates = [trimmed, basenameFirstToken(trimmed)];
    for (const def of Object.values(AGENT_REGISTRY)) {
        const variants = [
            [def.command, ...def.args],
            ...(def.aliases ?? []),
        ];
        for (const variant of variants) {
            const exact = variant.join(' ');
            for (const candidate of candidates) {
                if (candidate === exact)
                    return def;
                if (candidate.startsWith(exact + ' '))
                    return def;
            }
        }
    }
    return null;
}
/** Replace the command's first token with its path basename (handles `/` and `\`). */
function basenameFirstToken(command) {
    const spaceAt = command.indexOf(' ');
    const head = spaceAt === -1 ? command : command.slice(0, spaceAt);
    const rest = spaceAt === -1 ? '' : command.slice(spaceAt);
    const base = head.split(/[/\\]/).pop() ?? head;
    return base + rest;
}
//# sourceMappingURL=registry.js.map